import { type CSSObject, type Theme } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

import { focusRing } from "theme/theme.helpers";

/**
 * Figma's `Pagination/Item` (frame `15148:16766`): a 24px bordered box with `pagination/item/container/p`
 * 4 either side, so a three-digit page grows to 34px rather than clipping.
 */
export const PAGINATION_ITEM_SIZE = 24;

/**
 * `pagination/item/container/icon`. Set as a box, not MUI's `fontSize`: the glyphs are `assets` SVGs
 * with their own `width`/`height` attributes. MUI's -8px margin is for its 20px glyph, and goes too.
 */
export const PAGINATION_ITEM_ICON_SIZE = 16;

/**
 * Figma's `action/hoverBackground`. The theme sets no `action` palette, and MUI's own hover tint is a
 * translucent black rather than this grey. Exported so the Storybook page paints its hovered cell from
 * this rule rather than a copy of it.
 */
export const paginationItemHover = (theme: Theme): CSSObject => ({
  backgroundColor: theme.palette.grey[100],
});

/**
 * Every item is stated on `root`, because the ellipsis is a `MuiPaginationItem` root too — the
 * `ellipsis` key below, applied after `root` at equal specificity, takes the box back off it.
 */
export const MuiPaginationItem: ComponentOverrides["MuiPaginationItem"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...theme.typography.body2,
      minWidth: PAGINATION_ITEM_SIZE,
      height: PAGINATION_ITEM_SIZE,
      margin: 0,
      padding: theme.spacing(0, 0.5),
      borderRadius: theme.shape.small,
      border: `1px solid ${theme.palette.border.secondary}`,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[1],
      color: theme.palette.text.primary,
      "&:hover": paginationItemHover(theme),
      "&.Mui-focusVisible": {
        backgroundColor: theme.palette.background.paper,
        boxShadow: focusRing(theme.palette.primary.main),
      },
      "&.Mui-selected": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.lighter,
        "&:hover, &.Mui-focusVisible": { backgroundColor: theme.palette.primary.lighter },
        // Nested to match MUI's own `.Mui-selected.Mui-disabled`, which out-ranks the rule below.
        "&.Mui-disabled": {
          color: theme.palette.text.disabled,
          borderColor: theme.palette.border.secondary,
          backgroundColor: theme.palette.grey[100],
        },
      },
      // MUI fades a disabled item to `disabledOpacity`; Figma greys the fill and the label instead.
      "&.Mui-disabled": {
        opacity: 1,
        color: theme.palette.text.disabled,
        backgroundColor: theme.palette.grey[100],
      },
    }),
    ellipsis: {
      minWidth: 0,
      padding: 0,
      border: "none",
      backgroundColor: "transparent",
      boxShadow: "none",
    },
    icon: { width: PAGINATION_ITEM_ICON_SIZE, height: PAGINATION_ITEM_ICON_SIZE, margin: 0 },
  },
};

/** `pagination/item/container/spacing`: MUI spaces items with a 3px margin each side instead. */
export const MuiPagination: ComponentOverrides["MuiPagination"] = {
  styleOverrides: {
    ul: ({ theme }) => ({ gap: theme.spacing(1) }),
  },
};
